// @ts-ignore - JS module without type declarations
import { PLACE_RESULT_TYPE, PROVIDER_RESULT_TYPE } from 'utils/constants';

import type { SpecialtyData, SubspecialtyData } from 'types/common.types';

/**
 * Type for specialty type field ('P' for Provider, 'F' for Facility/Place)
 */
type SpecialtyType = 'P' | 'F';

/**
 * Interface for subspecialty objects
 * Extends SubspecialtyData but makes parentSpecialtyId and isScored optional for flexibility
 */
interface Subspecialty extends Partial<SubspecialtyData> {
  subspecialtyId: number;
  subspecialtyName: string;
  parentSpecialtyId?: number;
  description?: string;
  isScored?: boolean;
}

/**
 * Interface for specialty objects
 * Extends SpecialtyData but adds type field for provider/place distinction
 */
interface Specialty extends Omit<SpecialtyData, 'subspecialties'> {
  specialtyId: number;
  specialtyName: string;
  type: SpecialtyType;
  description?: string;
  subspecialties: Subspecialty[];
}

/**
 * Interface for classification objects (facility types and focus areas)
 */
interface Classification {
  emboldId: number | string;
  name: string;
  description?: string;
}

/**
 * Return type for processSpecialties function
 */
interface ProcessSpecialtiesResult {
  specialtiesMap: Record<string, Record<number, Specialty>>;
  subspecialtiesMap: Record<string, Record<number, Subspecialty>>;
  specialtiesNameMap: Record<string, Record<string, Specialty>>;
  subspecialtiesNameMap: Record<string, Record<string, Subspecialty>>;
}

/**
 * Return type for mapFacilityClassification function
 */
interface MapFacilityClassificationResult {
  classificationIdMap: Record<number | string, Classification>;
  classificationNameMap: Record<string, Classification>;
}

// eslint-disable-next-line import/prefer-default-export
export const processSpecialties = (
  specialties: Specialty[] = []
): ProcessSpecialtiesResult | null => {
  if (!specialties?.length) return null;
  const typeMap: Record<SpecialtyType, string> = {
    P: PROVIDER_RESULT_TYPE,
    F: PLACE_RESULT_TYPE,
  };
  const specialtiesMap: Record<string, Record<number, Specialty>> = {
    [PROVIDER_RESULT_TYPE]: {},
    [PLACE_RESULT_TYPE]: {},
  };
  const subspecialtiesMap: Record<string, Record<number, Subspecialty>> = {
    [PROVIDER_RESULT_TYPE]: {},
    [PLACE_RESULT_TYPE]: {},
  };
  const specialtiesNameMap: Record<string, Record<string, Specialty>> = {
    [PROVIDER_RESULT_TYPE]: {},
    [PLACE_RESULT_TYPE]: {},
  };
  const subspecialtiesNameMap: Record<string, Record<string, Subspecialty>> = {
    [PROVIDER_RESULT_TYPE]: {},
    [PLACE_RESULT_TYPE]: {},
  };
  specialties.forEach((specialty) => {
    const resultType = typeMap[specialty.type];
    specialtiesMap[resultType][specialty.specialtyId] = specialty;
    specialtiesNameMap[resultType][specialty.specialtyName.toLowerCase()] = specialty;
    if (specialty.subspecialties.length > 0) {
      specialty.subspecialties.forEach((subspecialty) => {
        // eslint-disable-next-line no-param-reassign
        subspecialty.parentSpecialtyId = specialty.specialtyId;
        subspecialtiesMap[resultType][subspecialty.subspecialtyId] = subspecialty;
        subspecialtiesNameMap[resultType][subspecialty.subspecialtyName.toLowerCase()] =
          subspecialty;
      });
    }
  });
  return { specialtiesMap, subspecialtiesMap, specialtiesNameMap, subspecialtiesNameMap };
};

export const mapFacilityClassification = (
  classifications: Classification[] = []
): MapFacilityClassificationResult => {
  const classificationIdMap: Record<number | string, Classification> = {};
  const classificationNameMap: Record<string, Classification> = {};
  classifications.forEach((classification) => {
    classificationIdMap[classification.emboldId] = classification;
    classificationNameMap[classification.name.toLowerCase()] = classification;
  });
  return { classificationIdMap, classificationNameMap };
};
